import React from 'react';
import styled from 'styled-components';
import { StaticQuery, graphql } from 'gatsby';

import { Section, Container, BlurredBg } from '@components/global';
import Polaroid from '@common/Polaroid';
import ScrollMotion from '@common/ScrollMotion';

import { ReactComponent as Squiggle } from '@static/squiggles/squiggle-1.svg';
import { ReactComponent as ArrowDownSquiggle } from '@static/squiggles/arrow-down.svg';
import AnimatedStroke from '@common/AnimatedStroke';

const Header = () => (
  <StaticQuery
    query={graphql`
      query {
        image_one: file(
          sourceInstanceName: { eq: "polaroid" }
          name: { eq: "polaroid-1" }
        ) {
          childImageSharp {
            fluid(maxWidth: 560, quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }

        image_two: file(
          sourceInstanceName: { eq: "polaroid" }
          name: { eq: "polaroid-2" }
        ) {
          childImageSharp {
            fluid(maxWidth: 640, quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    `}
    render={data => (
      <Section style={{ paddingTop: 96 }}>
        <Container>
          <Grid>
            <BlurredBg>
              <AnimatedStroke
                animProps={{
                  delay: 1000,
                  duration: 400,
                }}
                svg={<StyledArrowDownSquiggle />}
              />
              <Title>
                Your environment
                <br />
                defines your hair.
              </Title>
              <CursivePSmall>
                Professional haircare that goes beyond the basics. <br />
                Tackle environmental concerns that affect your hair daily.
              </CursivePSmall>
            </BlurredBg>
            <div style={{ position: 'relative' }}>
              <AnimatedStroke
                animProps={{ delay: 1000 }}
                svg={<StyledSquiggle />}
              />
              <ScrollMotion
                rotate={{ start: 1.5, end: 3 }}
                translate={{ start: 0, end: -64 }}
                enableEnterAnim
              >
                <StyledPolaroidOne
                  fluid={data.image_one.childImageSharp.fluid}
                />
              </ScrollMotion>
            </div>
            <div style={{ position: 'relative' }}>
              <ScrollMotion
                rotate={{ start: -2.2, end: -4 }}
                translate={{ start: 0, end: -64 }}
                enableEnterAnim
              >
                <StyledPolaroidTwo
                  fluid={data.image_two.childImageSharp.fluid}
                />
              </ScrollMotion>
            </div>

            <BlurredBg style={{ margin: '10rem 0' }}>
              <h3>
                Pioneering research that delivers unmatched haircare in
                challenging water environments. <br />
                Trusted by stylists for exacting results.
              </h3>
              <CursiveP>
                Designed with professionals
                <br />
                For professional results.
              </CursiveP>
            </BlurredBg>
          </Grid>
        </Container>
      </Section>
    )}
  />
);

const Grid = styled.div`
  display: grid;
  grid-template-columns: 9fr 8fr;
  grid-row-gap: 2rem;
  grid-column-gap: 8rem;

  > :nth-child(2),
  > :nth-child(3) {
    grid-row: auto / span 2;
  }

  @media (max-width: ${props => props.theme.screen.md}) {
    grid-template-columns: minmax(0, 2fr);
    grid-column-gap: 0;

    > * {
      grid-column: span 2;
    }

    > :nth-child(2),
    > :nth-child(3) {
      grid-row: auto;
      grid-column: auto / span 1;
    }
  }
`;

const CursivePSmall = styled.p`
  font-family: ${props => props.theme.font.cursive};
  color: ${props => props.theme.color.blue[1].light};
  ${props => props.theme.font_size.small};
  margin-top: 3rem;
  line-height: 160%;

  @media (max-width: ${props => props.theme.screen.xs}) {
    display: none;
  }
`;

const CursiveP = styled.p`
  font-family: ${props => props.theme.font.cursive};
  color: ${props => props.theme.color.blue[1].light};
  ${props => props.theme.font_size.large};
  margin-top: 3rem;
  line-height: 160%;

  @media (max-width: ${props => props.theme.screen.sm}) {
    text-align: center;
  }
`;

const Title = styled.h1`
  margin: 2rem 0;
  line-height: 108%;

  .light {
    color: ${props => props.theme.color.blue[1].light};
  }

  @media (max-width: ${props => props.theme.screen.sm}) {
    text-align: center;
  }
`;

const StyledSquiggle = styled(Squiggle)`
  position: absolute;
  left: -36%;
  top: 52%;

  @media (max-width: ${props => props.theme.screen.lg}) {
    left: -52%;
    top: 40%;
  }

  @media (max-width: ${props => props.theme.screen.md}) {
    left: 50%;
    top: 36%;
  }

  @media (max-width: ${props => props.theme.screen.sm}) {
    left: 30%;
    top: 16%;
  }
`;

const StyledArrowDownSquiggle = styled(ArrowDownSquiggle)`
  filter: url(#grain);
  position: absolute;
  left: 104%;
  top: 75%;

  @media (max-width: ${props => props.theme.screen.md}) {
    left: 50%;
    transform: translateX(-50%);
    top: 110%;
  }
`;

const StyledPolaroidOne = styled(Polaroid)`
  @media (max-width: ${props => props.theme.screen.md}) {
    position: relative;
    left: -38%;
    transform: rotate(-9deg);
    margin-top: 6rem;
  }

  @media (max-width: ${props => props.theme.screen.xs}) {
    min-width: 10rem;
  }
`;

const StyledPolaroidTwo = styled(Polaroid)`
  @media (max-width: ${props => props.theme.screen.md}) {
    position: absolute;
    min-width: 30rem;
    transform: rotate(8deg) translate(-32%);
  }

  @media (max-width: ${props => props.theme.screen.xs}) {
    min-width: 17rem;
  }
`;

export default Header;
